import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";

interface GoogleSignupTosModalProps {
  show: boolean;
  onConfirm: (tosAccepted: boolean, marketingOptIn: boolean) => void;
  onCancel: () => void;
}

/**
 * Modal component for Google signup that requires users to accept Terms & Conditions
 * before proceeding with Google OAuth flow.
 */
const GoogleSignupTosModal: React.FC<GoogleSignupTosModalProps> = ({
  show,
  onConfirm,
  onCancel,
}) => {
  const [tosAccepted, setTosAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!show) {
      setTosAccepted(false);
      setMarketingOptIn(false);
    }
  }, [show]);

  // Focus management: focus checkbox when modal opens
  useEffect(() => {
    if (show) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        const checkbox = document.getElementById("google-signup-tos-checkbox");
        checkbox?.focus();
      }, 100);
    }
  }, [show]);

  // Handle ESC key to cancel
  useEffect(() => {
    if (!show) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show, onCancel]);

  const handleConfirm = () => {
    if (tosAccepted) {
      onConfirm(tosAccepted, marketingOptIn);
    }
  };

  const titleId = "google-signup-tos-modal-title";
  const canContinue = tosAccepted;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size="lg"
      centered
      role="dialog"
      aria-labelledby={titleId}
      aria-modal="true"
    >
      <Modal.Header closeButton>
        <Modal.Title id={titleId}>Accept Terms & Conditions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <p className="mb-3">
            Before continuing with Google signup, please review and accept our Terms of Service and
            Privacy Policy.
          </p>

          {/* [2026-02-07] style-extraction: moved inline styles to _auth.scss */}
          {/* Terms of Service and Privacy Policy */}
          <div className="mb-4">
            <div className="d-flex align-items-start">
              <input
                type="checkbox"
                id="google-signup-tos-checkbox"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                className="me-2 mt-1 auth-checkbox-input"
                aria-required="true"
              />
              <label
                htmlFor="google-signup-tos-checkbox"
                className="u-P1-18 auth-checkbox-label"
              >
                I agree to the{" "}
                <a href="https://uavtradezone.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://uavtradezone.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                *
              </label>
            </div>
          </div>

          {/* Marketing Opt-in */}
          <div className="mb-3">
            <div className="d-flex align-items-start">
              <input
                type="checkbox"
                id="google-signup-marketing-checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="me-2 mt-1 auth-checkbox-input"
              />
              <label
                htmlFor="google-signup-marketing-checkbox"
                className="u-P1-18 auth-checkbox-label"
              >
                I would like to receive marketing communications and product updates
              </label>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          ref={continueButtonRef}
          variant="primary"
          onClick={handleConfirm}
          disabled={!canContinue}
        >
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GoogleSignupTosModal;

